// SSE consumer for /api/ai/doors/build — the Activity Doors generation
// endpoint. Mirrors lib/scenario-builder/streamClient.ts: fetch with the
// Supabase JWT, parse SSE text-delta events, surface done/error.
//
// The raw streamed text is the model's output verbatim (INVENTED/NO FIT line +
// HTML); parse it with lib/doors/parseBuildOutput.ts. During the stream,
// partialHtml() gives the caller a paintable document as it materialises.
//
// Three event types arrive: `thinking` (reasoning-character count, the only
// signal during Opus's opening think), `text-delta` (the document), and one
// terminal `done` or `error`. Callers do not own the stream's lifetime —
// lib/doors/buildRuns.ts does — because a build outlives the component that
// started it.

import { supabase } from '../lib/supabase';
import { classifyTransportFailure, messageForStatus } from '../lib/transportFailure';
import type { DoorIntake, DoorKey } from '../components/course-builder/slides/slideTypes';

const ENDPOINT = '/api/ai/doors/build';

export class DoorBuildError extends Error {
  constructor(public code: string, message: string, public requested?: number) {
    super(message);
    this.name = 'DoorBuildError';
  }
}

export interface DoorBuildResult {
  /** The model's raw output — parse with parseDoorBuildOutput / parseRefineOutput. */
  fullText: string;
  generationId?: string;
  creditsDebited?: number;
  stopReason?: string;
  model: string;
}

interface CommonOptions {
  signal?: AbortSignal;
  /** Fires per streamed text chunk with the accumulated raw text so far. */
  onText?: (accumulated: string) => void;
  /**
   * Fires while the model is still thinking, with the reasoning characters
   * produced so far. Opus thinks before it writes, so this is the ONLY signal
   * available during the opening stretch of a build — without it the UI has
   * nothing to show for minutes and reads as hung.
   */
  onThinking?: (chars: number) => void;
}

interface BuildOptions extends CommonOptions {
  mode: 'build';
  door: DoorKey;
  intake: Pick<DoorIntake, 'context' | 'goal' | 'length' | 'world'>;
  lessonSource?: string;
}

interface RefineOptions extends CommonOptions {
  mode: 'refine';
  previousHtml: string;
  refinementText: string;
}

/** A refine whose request is "move this build into another world". The client
 *  names the world; the endpoint owns its brief and the restyle instruction. */
interface RestyleOptions extends CommonOptions {
  mode: 'restyle';
  previousHtml: string;
  world: string;
}

export type DoorStreamOptions = BuildOptions | RefineOptions | RestyleOptions;

/** Turn a transport failure into a DoorBuildError the author can read —
 *  "Failed to fetch" says nothing about whether their work survived. Anything
 *  unrecognised is passed through untouched. */
function asTransportError(err: unknown): unknown {
  if (err instanceof DoorBuildError) return err;
  const failure = classifyTransportFailure(err);
  return failure ? new DoorBuildError(failure.code, failure.message) : err;
}

/**
 * Run one door build or refinement to completion, returning the model's raw
 * output. Rejects rather than returning a partial or empty result — see the
 * terminal checks at the end.
 *
 * Callers do not own the lifetime of what they start here: pass a signal from
 * lib/doors/buildRuns, which keeps the run alive across the component's
 * unmounts and aborts it only on an explicit cancel.
 */
export async function streamDoorBuild(opts: DoorStreamOptions): Promise<DoorBuildResult> {
  const session = await supabase.auth.getSession();
  const token = session.data.session?.access_token;
  if (!token) throw new DoorBuildError('unauthorized', 'Not signed in');

  const body =
    opts.mode === 'build'
      ? {
          mode: 'build',
          door: opts.door,
          intake: opts.intake,
          lessonSource: opts.lessonSource,
        }
      : opts.mode === 'restyle'
        ? {
            mode: 'restyle',
            previousHtml: opts.previousHtml,
            world: opts.world,
          }
        : {
            mode: 'refine',
            previousHtml: opts.previousHtml,
            refinementText: opts.refinementText,
          };

  let res: Response;
  try {
    res = await fetch(ENDPOINT, {
      method: 'POST',
      headers: {
        'Content-Type': 'application/json',
        Authorization: `Bearer ${token}`,
      },
      body: JSON.stringify(body),
      signal: opts.signal,
    });
  } catch (e) {
    throw asTransportError(e);
  }

  if (!res.ok) {
    let errBody: any = null;
    try {
      errBody = await res.json();
    } catch { /* not JSON */ }
    const code = errBody?.error || `http_${res.status}`;
    const message =
      errBody?.message || messageForStatus(res.status) || `Request failed (${res.status})`;
    throw new DoorBuildError(code, message, errBody?.requested);
  }

  if (!res.body) throw new DoorBuildError('no_stream', 'Server returned no stream');

  const reader = res.body.getReader();
  const decoder = new TextDecoder();
  let buffer = '';
  let fullText = '';
  let generationId: string | undefined;
  let creditsDebited: number | undefined;
  let stopReason: string | undefined;
  let thoughtChars = 0;
  // The endpoint sends exactly one `done` when the generation lands. Its
  // ABSENCE is the tell that the connection died mid-flight, which is
  // otherwise indistinguishable from a clean end-of-stream.
  let sawDone = false;

  try {
    while (true) {
      const { done, value } = await reader.read();
      if (done) break;
      buffer += decoder.decode(value, { stream: true });

      const events = buffer.split('\n\n');
      buffer = events.pop() ?? '';

      for (const evt of events) {
        const line = evt.split('\n').find((l) => l.startsWith('data: '));
        if (!line) continue;
        const payload = line.slice(6).trim();
        if (!payload) continue;

        let parsed: any;
        try {
          parsed = JSON.parse(payload);
        } catch {
          continue;
        }

        if (parsed.type === 'text-delta' && typeof parsed.delta === 'string') {
          fullText += parsed.delta;
          opts.onText?.(fullText);
        } else if (parsed.type === 'thinking' && typeof parsed.chars === 'number') {
          thoughtChars = parsed.chars;
          opts.onThinking?.(thoughtChars);
        } else if (parsed.type === 'done') {
          sawDone = true;
          generationId = parsed.generationId;
          creditsDebited = parsed.creditsDebited;
          stopReason = parsed.stopReason;
        } else if (parsed.type === 'error') {
          throw new DoorBuildError('upstream_error', parsed.message || 'Upstream error');
        }
      }
    }
  } catch (e) {
    throw asTransportError(e);
  } finally {
    try { reader.releaseLock(); } catch { /* noop */ }
  }

  // A stream that ends without `done` was cut — by an idle proxy, a lost
  // connection, the function's own duration ceiling. Returning what arrived
  // would hand the caller either an empty string or a truncated document, and
  // parseDoorBuildOutput turns BOTH into a cheerful `{ kind: 'built' }` — an
  // empty `html` that silently re-renders as the un-built stub, with a full
  // build's credits already spent and nothing said about it.
  if (!sawDone) {
    throw new DoorBuildError(
      'stream_interrupted',
      fullText.trim()
        ? 'The connection dropped part-way through, so the build is incomplete. Nothing was changed — try again.'
        : 'The connection dropped before the build produced anything. Nothing was changed — try again.',
    );
  }
  if (!fullText.trim()) {
    throw new DoorBuildError('empty_build', 'The build came back empty. Try again.');
  }

  return { fullText, generationId, creditsDebited, stopReason, model: 'claude-opus-5' };
}
